import React, { useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";
import { AirplaneTilt, Bed } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import useStayCities from "@/hooks/useStayCities";
import useRevealOnScroll, { staggerDelay } from "@/hooks/useRevealOnScroll";
import { formatDate, formatDateRange } from "@/lib/format";
import { responsiveImage } from "@/lib/images";
import { localeOf, pick } from "@/lib/languages";
import { searchLink, track } from "@/lib/recommendations";
import { cn } from "@/lib/utils";
import { flightsLink, staysLink, worldCards } from "@/lib/worldDestinations";
import { worldL } from "@/i18n/world";
import "@/styles/motion.css";

/**
 * Toukky's world, as photo cards: the twelve cities of
 * lib/worldDestinations (every continent), each opening a real flight search
 * to that city's airport, and the stays Toukky lists there when it lists any.
 *
 *   <WorldDestinations
 *     origin="DSS"            // the visitor's airport (IATA) or ""; a card
 *                             // searches from it unless it is that city
 *     fares={map}             // optional: Map or object CODE -> { card, section }
 *                             // (a real fare from /recommendations/home); its
 *                             // city's card shows "Flights from X" and its
 *                             // dates, opens that dated search, and comes first.
 *                             // Show worldL.faresNote beside the band then.
 *     stays={cities}          // optional: stay cities (lib/worldDestinations
 *                             // stayCities). Left out, the component reads
 *                             // them itself (GET /hotels, Toukky's own list);
 *                             // null or [] shows no stays.
 *     heading={w.heading}     // optional h2 (and `sub` under it) from
 *     sub={w.sub}             // pick(worldL, lang); without one, the caller's
 *                             // section titles the list (Home's HomeSection).
 *     bleed                   // default true: on a phone the row scrolls
 *                             // edge to edge through a 16px page gutter.
 *     dates={{ date, return_date }} // YYYY-MM-DD, pass defaultTripDates()
 *                             // (lib/worldDestinations): a card without a
 *                             // fare opens a search on these dates. Without
 *                             // dates the airlines are not asked, so every
 *                             // caller passes them (Home, the flights page).
 *   />
 *
 * Each card is `world-destination-CODE`, its flight link `world-flights-CODE`
 * and its stays link `world-stays-CODE`. No price, count or popularity is
 * shown unless real data gave it; the photos are decorative (alt="", the
 * city is in the card), lazy, in a fixed 3:4 box, and rise into place one
 * after the other on the first scroll (styles/motion.css: still under
 * reduced motion).
 */

// A row of large cards on a phone, three across from 640px, four from
// 768px (three rows, not four tall ones, on a tablet), six from 1280px (in
// the 1240px page).
const PHOTO_WIDTHS = [320, 480, 640, 960];
const PHOTO_SIZES = "(min-width: 1280px) 190px, (min-width: 768px) 23vw, (min-width: 640px) 31vw, 72vw";

const regionNames = new Map();
function countryName(code, lang) {
  if (!code) return "";
  try {
    const locale = localeOf(lang);
    if (!regionNames.has(locale)) regionNames.set(locale, new Intl.DisplayNames([locale], { type: "region" }));
    const name = regionNames.get(locale).of(code);
    return name && name !== code ? name : "";
  } catch {
    return "";
  }
}

export default function WorldDestinations({ origin = "", fares, stays, dates, heading, sub, bleed = true, className, "data-testid": testId = "world-destinations" }) {
  const { lang } = useSettings();
  const l = pick(worldL, lang);
  const fetched = useStayCities({ enabled: stays === undefined });
  const cities = stays === undefined ? fetched : stays || [];
  const cards = worldCards({ fares, cities });
  const reveal = useRevealOnScroll();
  const headingId = useId();

  const list = (
    <ul ref={reveal} className={cn(
      "tk-stagger flex snap-x snap-mandatory gap-3 overflow-x-auto overflow-y-hidden py-1 no-scrollbar",
      bleed && "-mx-4 scroll-px-4 px-4",
      "sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible sm:px-0 sm:py-0 md:grid-cols-4 xl:grid-cols-6",
    )}>
      {cards.map((card, index) => (
        <li key={card.destination.code} className="w-[72vw] max-w-[280px] shrink-0 snap-start sm:w-auto sm:max-w-none"
          style={{ "--tk-delay": staggerDelay(index) }}>
          <WorldCard card={card} origin={origin} dates={dates} l={l} lang={lang} />
        </li>
      ))}
    </ul>
  );

  if (!heading) return <div className={className} data-testid={testId}>{list}</div>;
  return (
    <section aria-labelledby={headingId} className={className} data-testid={testId}>
      <div className="min-w-0 max-w-[65ch]">
        <h2 id={headingId} className="text-heading-lg text-touki-black">{heading}</h2>
        {sub ? <p className="mt-1 text-body-sm text-touki-muted text-pretty">{sub}</p> : null}
      </div>
      <div className="mt-4 sm:mt-5">{list}</div>
    </section>
  );
}

/** A fare seen once (half of its card on screen): an anonymous event, as on the flights page. */
function useImpression(fare, origin) {
  const ref = useRef(null);
  const section = fare?.section;
  const code = fare?.card?.destination?.code;
  useEffect(() => {
    const node = ref.current;
    if (!section || !node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        track("impression", { section, origin, destination: code });
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [section, origin, code]);
  return ref;
}

function WorldCard({ card, origin, dates, l, lang }) {
  const { formatPrice } = useSettings();
  const { destination, stays, fare } = card;
  const { code } = destination;
  const name = l.cities[code] || destination.city;
  const country = countryName(destination.country, lang);
  const ref = useImpression(fare, origin);
  const href = fare ? searchLink(fare.card.search) : flightsLink(destination, origin, dates);
  const onClick = fare ? () => track("click", { section: fare.section, origin, destination: fare.card.destination?.code }) : undefined;
  // No number when the list read was not the whole inventory (hooks/useStayCities).
  const count = stays ? (stays.count == null ? l.staysLabel : l.stays(stays.count)) : "";
  const when = fare
    ? (fare.card.return ? formatDateRange(fare.card.depart, fare.card.return, { lang, weekday: false }) : formatDate(fare.card.depart, { lang, weekday: false }))
    : "";
  const pill = "inline-flex min-h-9 items-center gap-1.5 rounded-pill px-3 py-1 text-label-md font-semibold";
  const plane = <AirplaneTilt size={14} weight="bold" aria-hidden="true" className="shrink-0 rtl:-scale-x-100" />;

  return (
    <article ref={ref} className="tk-zoom group relative isolate grid h-full" data-testid={`world-destination-${code}`}>
      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-touki-dark [grid-area:1/1]">
        {/* `lift`: drawn taller from its foot, so the subject sits above the caption. */}
        <img {...responsiveImage(destination.photo, PHOTO_WIDTHS, PHOTO_SIZES)} alt="" width={600} height={800}
          loading="lazy" decoding="async" className={cn("tk-zoom-photo w-full object-cover", destination.lift ? "absolute inset-x-0 bottom-0" : "h-full")}
          style={destination.lift ? { height: `${destination.lift * 100}%` } : undefined} />
      </div>
      {/* Not positioned: the city link's ::after covers the whole card and
          draws its focus ring round the card, on the page (the landing
          theme's ring on the name itself would sit red on ink). The
          words sit on at least 60% ink, however tall they run and however
          bright the photo (white on it is 5:1 or more), fading out above. */}
      <div className="z-raised flex min-w-0 flex-col self-end text-white [grid-area:1/1]">
        <div aria-hidden="true" className="h-12 bg-gradient-to-t from-touki-black/60 to-transparent" />
        <div className="flex min-w-0 flex-col rounded-b-lg bg-gradient-to-t from-touki-black/80 to-touki-black/60 px-3 pb-3 sm:px-4 sm:pb-4">
          <h3 className="font-heading text-heading-md font-bold text-white [overflow-wrap:anywhere]">
            <Link to={href} onClick={onClick} aria-label={l.flightsTo(name)} data-testid={`world-flights-${code}`}
              className="rounded-lg focus-visible:!outline-none after:absolute after:inset-0 after:rounded-lg after:content-[''] focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-[color:var(--tk-focus)]">
              {name}
            </Link>
          </h3>
          {country ? <p className="text-label-md text-touki-on-ink-muted">{country}</p> : null}
          {fare ? <p className="mt-1.5 text-label-md tabular-nums text-touki-on-ink-muted">{when}</p> : null}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {fare ? (
              // A long price wraps inside a rounded box rather than a stretched pill.
              <p className={cn(pill, "rounded-md bg-white text-touki-black tabular-nums leading-snug")} data-testid={`reco-card-${fare.section}-${fare.card.destination?.code}`}>
                {plane}<span data-testid="reco-price">{l.flightsFrom(formatPrice(fare.card.price_xof))}</span>
              </p>
            ) : (
              // What the card opens; the card itself is the link.
              <span aria-hidden="true" className={cn(pill, "bg-white text-touki-black transition-colors duration-fast group-hover:bg-touki-light")}>
                {plane}{l.flights}
              </span>
            )}
            {stays ? (
              <Link to={staysLink(stays.city)} aria-label={l.staysAt(count, name)} data-testid={`world-stays-${code}`}
                className={cn(pill, "tk-on-ink relative z-raised border border-white/60 bg-touki-black/40 text-white tabular-nums transition-colors duration-fast hover:bg-touki-dark-hover before:absolute before:-inset-1 before:content-['']")}>
                <Bed size={14} weight="bold" aria-hidden="true" className="shrink-0" />{count}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}
